import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseCameraReturn {
  /** Whether the camera stream is currently active */
  isActive: boolean;
  /** The MediaStream object, or null if not active */
  stream: MediaStream | null;
  /** Start the camera */
  start: () => Promise<void>;
  /** Stop the camera */
  stop: () => void;
  /** Toggle camera on/off */
  toggle: () => Promise<void>;
  /** Error message, if any */
  error: string | null;
}

/**
 * Hook to manage the user's webcam video stream.
 * Provides start/stop/toggle and exposes the MediaStream for a <video> element.
 */
export function useCamera(): UseCameraReturn {
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
      setStream(null);
      setIsActive(false);
    }
  }, []);

  const start = useCallback(async () => {
    setError(null);

    // Already running
    if (streamRef.current) {
      return;
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = mediaStream;
      setStream(mediaStream);
      setIsActive(true);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Unable to access camera';
      setError(message);
      setIsActive(false);
    }
  }, []);

  const stop = useCallback(() => {
    stopTracks();
    setError(null);
  }, [stopTracks]);

  const toggle = useCallback(async () => {
    if (streamRef.current) {
      stop();
    } else {
      await start();
    }
  }, [start, stop]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopTracks();
    };
  }, [stopTracks]);

  return { isActive, stream, start, stop, toggle, error };
}
